<script setup>
import { computed, ref } from 'vue';
import { parseISO, format } from 'date-fns';
import { useForm } from 'vee-validate';
import CheckboxFormField from 'components/CheckboxFormField.vue';
import AddressForm from 'components/forms/AddressForm.vue';
import axios from 'axios';
import { useUserForm } from 'src/composables/useUserForm';
import { useStore } from 'vuex';
import { useAnalytics } from 'src/composables/analytics';

const props = defineProps({
  additionalData: {
    type: Object,
    required: true,
  }
});

const emit = defineEmits(['finished']);

const store = useStore();

const previousRequest = ref(null);
const previousRequestDate = computed(() => (
  previousRequest.value
    ? format(parseISO(previousRequest.value.createdDate), 'yyyy/MM/dd')
    : null));
const previousRequestTime = computed(() => (
  previousRequest.value
    ? format(parseISO(previousRequest.value.createdDate), 'HH:mm')
    : null
));

async function checkForPreviousRequest() {
  previousRequest.value = await store.dispatch('user/getSampleRequestForPRD', props.additionalData.prd);
}

checkForPreviousRequest();

const { trackEvent } = useAnalytics();

const user = computed(() => store.state.user.activeUser);
const isValidated = computed(() => store.getters['user/isValidated']);

const regionSelectionNecessary = computed(() => !(user.value && isValidated.value));

const eventLabel = computed(() => ({
  prd: props.additionalData.prd,
  productName: props.additionalData.product_name,
  selectedIndustry: props.additionalData.selected_industry ? props.additionalData.selected_industry : 'unknown'
}));

const {
  handleSubmit,
  values: formValues,
  resetForm,
} = useForm({
  initialValues: {
    address: {
      customShippingAddress: false,
    }
  }
});

const {
  isSending,
  message,
  formSecurityToken,
  consentHintNecessary,
  links,
  hasMessage,
  contactRegions,
} = useUserForm({
  regionSelectionNecessary,
});

const contactRegionOptions = computed(() => contactRegions.value.map((contactRegion) => ({
  ...contactRegion,
  value: contactRegion.code
})));

function reset() {
  isSending.value = false;
  resetForm({
    address: {
      customer: {},
      customShippingAddress: false,
      shipping: {}
    },
    reason: null
  });
}

const submit = handleSubmit(async (values) => {
  isSending.value = true;

  const data = {
    ...props.additionalData,
    formSecurityToken: formSecurityToken.value,
    reason: values.reason,
    address: values.address,
    recipient: isValidated.value ? [user.value.customerService.email, user.value.accountManager.email] : null
  };

  ['firstName', 'lastName', 'company', 'email', 'region', 'subRegion'].forEach((f) => {
    data[f] = user.value && user.value[f] ? user.value[f] : values[f];
  });

  try {
    await axios.post(`${location.origin}/api/v1/message/requestSampleForm`, data);
    emit('finished');

    trackEvent(
      'Form',
      'Request a sample',
      eventLabel.value
    );

    reset();
  } catch (e) {
    message.value = {
      type: 'error',
      content: e.response?.data?.message ?? e.message
    };
  }

  isSending.value = false;
});
</script>

<template>
  <form
    @submit="submit"
    class="ce-modalContent ce-requestSampleForm">
    <div
      v-if="previousRequest"
      class="ce-requestSampleForm-prevRequestHint mb4 ms-1">
      <fa-icon
        class="mr1"
        icon="exclamation-circle" />
      {{ $t('requestSampleForm.prevRequestHint', { date: previousRequestDate, time: previousRequestTime }) }}
    </div>

    <alert
      class="mb3"
      v-if="hasMessage"
      :message="message.content"
      :type="message.type"
      @close="message = {}" />

    <!-- Readonly user info -->
    <div>
      <div class="flex flex-wrap">
        <div class="ce-field col-12 xs-col-6 xs-pr3">
          <label>{{ $t('contactForm.firstName') }}</label>
          <input
            type="text"
            readonly
            :value="user.firstName">
        </div>
        <div class="ce-field col-12 xs-col-6 xs-pl3">
          <label>{{ $t('contactForm.lastName') }}</label>
          <input
            type="text"
            readonly
            :value="user.lastName">
        </div>
      </div>
      <div class="ce-field">
        <label>{{ $t('contactForm.email') }}</label>
        <input
          type="text"
          readonly
          :value="user.email">
      </div>
      <div class="ce-field">
        <label>{{ $t('contactForm.company') }}</label>
        <input
          type="text"
          readonly
          :value="user.company">
      </div>
    </div>

    <!-- (Sub-) Region selection -->
    <div>
      <validated-select
        v-if="regionSelectionNecessary"
        name="region"
        :label="$t('contactForm.region')"
        :options="contactRegionOptions"
        rules="required"
        :use-key-only-value="false"
      />

      <validated-select
        v-if="regionSelectionNecessary && formValues.region && formValues.region.subRegions"
        name="subRegion"
        :label="$t('contactForm.subRegion')"
        :options="formValues.region.subRegions"
        rules="required"
        :use-key-only-value="false" />
    </div>

    <address-form
      :label="$t('requestSampleForm.customerAddress')"
      prefix="address.customer" />

    <div class="ce-field">
      <checkbox-form-field
        id="customShippingAddress"
        name="address.customShippingAddress">
        {{ $t('requestSampleForm.customShippingAddress') }}
      </checkbox-form-field>
    </div>

    <address-form
      v-if="formValues.address.customShippingAddress"
      :label="$t('requestSampleForm.shippingAddress')"
      prefix="address.shipping" />

    <validated-input
      name="reason"
      type="textarea"
      :label="$t('requestSampleForm.reason')"
      rules="required" />

    <div
      v-if="consentHintNecessary"
      class="ms-1 line-height-4">
      <i18n-t
        keypath="contactForm.consentHint"
        tag="p"
        scope="global">
        <a
          :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
          rel="noopener noreferrer">
          {{ $t('contactForm.dataProtectionPage') }}</a>
      </i18n-t>
    </div>

    <div class="mt4">
      <button
        class="ce-btn ce-btn-light ce-btn--fullWidth"
        @click.prevent="submit">
        <template v-if="isSending">
          <fa-icon
            icon="spinner"
            spin />
        </template>
        <template v-else>
          {{ $t('requestSampleForm.submitCta') }}
        </template>
      </button>
    </div>
  </form>
</template>

<style lang="less">
  @import (reference) "~styles/includes/base";

  .ce-requestSampleForm {
    .ce-requestSampleForm-prevRequestHint {
      color: @color-warn;
    }
  }
</style>
